/**
 * @fileoverview Modal component for creating prescriptions
 * Wraps PrescriptionForm for use in modal context with tabs
 */
import React from "react";
import PropTypes from "prop-types";
import { intl, DmsModal, DmsTabber } from "dmsbasereact";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { PrescriptionForm } from "../PrescriptionForm/PrescriptionForm";
import { PatientPrescriptionHistoryTab } from "../PatientPrescriptionHistoryTab";

/**
 * Modal for creating prescriptions
 * Opens with patient data pre-populated and prescription history tab
 */
export const PrescriptionModal = ({
  open,
  onClose,
  patient = null,
  onSuccess = null}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSuccess = () => {
    // Refresh data and close modal
    onSuccess?.();
    onClose?.();
  };

  if (!open) return null;

  const tabs = [
    {
      title: intl.get("NewPrescription").d("New Prescription"),
      component: (
        <Box sx={{ p: isMobile ? 1 : 2 }}>
          <PrescriptionForm
            isModal={true}
            onSuccess={handleSuccess}
            useCpfSearch={false}
            initialPatient={patient}
          />
        </Box>
      ),
    },
    {
      title: intl.get("PrescriptionHistory").d("Prescription History"),
      component: patient ? (
        <PatientPrescriptionHistoryTab patientPk={patient.pk} />
      ) : (
        <Box sx={{ p: 2, textAlign: "center" }}>
          {intl.get("SelectPatientFirst").d("Select a patient first")}
        </Box>
      ),
    },
  ];

  return (
    <DmsModal
      isVisible={open}
      onClose={(event, reason) => {
        // Prevent closing when clicking outside the modal
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        onClose();
      }}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <DmsModal.Header
        title={intl.get("Prescription").d("Prescription")}
        onClose={onClose}
      />
      <DmsModal.Body>
        <DmsTabber tabs={tabs} />
      </DmsModal.Body>
    </DmsModal>
  );
};

PrescriptionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  patient: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default PrescriptionModal;
